import { useState, useCallback } from 'react';

const STEP = 1000;

/**
 * Drag-and-drop priority reorder workflow shared by the warehouses list pages:
 * loading the full list for reordering, computing anchored 1000-step priorities
 * on drag end, and persisting them. Each page supplies its own fetch/save calls
 * since the endpoints, query params, and payload shapes differ.
 *
 * @param {() => Promise<object[]>} fetchSortItems - loads the full (unpaginated) list to reorder
 * @param {(updatedItems: object[]) => Promise<void>} saveOrder - persists the new priorities
 * @param {string} [priorityField] - field read to anchor the new priority sequence (also always sets `priority`)
 * @param {() => void} [onExit] - called when leaving sort mode (e.g. to invalidate query cache)
 */
export function usePriorityReorder({ fetchSortItems, saveOrder, priorityField = 'priority', onExit }) {
  const [sortMode, setSortMode] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [sortItems, setSortItems] = useState([]);

  const enterSortMode = useCallback(async () => {
    setSavingOrder(true);
    try {
      setSortItems(await fetchSortItems());
      setSortMode(true);
    } catch (e) {
      console.error('Failed to load items for sorting', e);
    } finally {
      setSavingOrder(false);
    }
  }, [fetchSortItems]);

  const exitSortMode = useCallback(() => {
    setSortMode(false);
    setSortItems([]);
    onExit?.();
  }, [onExit]);

  const handleDragEnd = useCallback(async ({ source, destination }) => {
    if (!destination || source.index === destination.index) return;
    const reordered = [...sortItems];
    const [moved] = reordered.splice(source.index, 1);
    reordered.splice(destination.index, 0, moved);

    // Redistribute priorities anchored to the current minimum with a fixed 1000-step,
    // leaving space for future insertions regardless of how many items are spanned.
    const priorities = reordered.map((item) => item[priorityField] ?? 0);
    const pMin = Math.min(...priorities);
    const newPriorities = pMin === 0
      ? reordered.map((_, i) => (i + 1) * STEP)
      : reordered.map((_, i) => pMin + i * STEP);
    const withUpdatedPriorities = reordered.map((item, i) => ({ ...item, [priorityField]: newPriorities[i], priority: newPriorities[i] }));

    setSortItems(withUpdatedPriorities);
    setSavingOrder(true);
    try {
      await saveOrder(withUpdatedPriorities);
    } catch (e) {
      console.error('Failed to save item order', e);
      setSortItems(sortItems); // revert on error
    } finally {
      setSavingOrder(false);
    }
  }, [sortItems, priorityField, saveOrder]);

  return { sortMode, savingOrder, sortItems, enterSortMode, exitSortMode, handleDragEnd };
}
